import { QuickVO } from 'quickvo-sdk-js';
import type {
  RoomUser,
  RoomOptions,
  SendMessageRevolve,
  UserTrack,
} from 'quickvo-sdk-js';
import type React from 'react';
import { useRef, useEffect, useCallback } from 'react';
import { isArray } from 'lodash-es';
import { useSessionContext } from '@/contexts/session/context';
import { isDebug, pushLog } from '@/scripts/utils/devUtils';
import type { UseChatMediaCallResult } from '../useChatMediaCall/types';
import type { MediaDevicesType, RoomUsersStreams } from './types';
import { MediaDeviceKind } from './types';
import { MediaType } from './types';

export function useQuickVO({
  chatMediaCall,
}: {
  chatMediaCall: UseChatMediaCallResult;
}) {
  const {
    session: { user },
  } = useSessionContext();
  const quickvoRef = useRef<QuickVO | null>(null);
  const [roomUsers, setRoomUsers] = useState<RoomUser[]>([]);
  const [roomUsersStreams, setRoomUsersStreams] = useState<RoomUsersStreams[]>([]);
  const [devices, setDevices] = useState<MediaDevicesType>({
    devicesVideoInput: [],
    devicesAudioInput: [],
    devicesAudioOutput: [],
  });

  const [refresh, setRefresh] = useState(false);

  const clearState = useCallback(() => {
    setRoomUsers([]);
    setRoomUsersStreams([]);
  }, []);

  const publish = useCallback((mediaTypes: MediaType | MediaType[]) => {
    return quickvoRef.current?.publish(isArray(mediaTypes) ? mediaTypes : [mediaTypes]);
  }, []);

  const stopPublish = useCallback((mediaTypes: MediaType | MediaType[]) => {
    return quickvoRef.current?.stopPublish(
      isArray(mediaTypes) ? mediaTypes : [mediaTypes]
    );
  }, []);

  const subscribe = useCallback((trackNames: string[]) => {
    return quickvoRef.current?.subscribe(trackNames);
  }, []);

  const stopSubscribe = useCallback((trackNames: string[]) => {
    return quickvoRef.current?.stopSubscribe(trackNames);
  }, []);

  const subscribeAllUser = useCallback(
    async (users: RoomUser[]) => {
      const trackNames = users.flatMap((user) =>
        !user.isSelf ? user.tracks?.map((track) => track.trackName) || [] : []
      );
      return subscribe(trackNames);
    },
    [subscribe]
  );

  const joinRoom = useCallback(
    async (roomOptions: RoomOptions) => {
      return quickvoRef.current?.joinRoom(roomOptions).then((roomUsers) => {
        return subscribeAllUser(roomUsers);
      });
    },
    [subscribeAllUser]
  );

  const quitRoom = useCallback(() => {
    clearState();
    return quickvoRef.current?.quitRoom();
  }, [clearState]);

  const quitRoomEx = useCallback(() => {
    clearState();
    return quickvoRef.current?.quitRoomEx();
  }, [clearState]);

  const getVolume = useCallback((userId: string, mediaType: MediaType) => {
    return quickvoRef.current?.getVolume(userId, mediaType);
  }, []);

  const setVolume = useCallback(
    (userId: string, mediaType: MediaType, gain: number) => {
      return quickvoRef.current?.setVolume(userId, mediaType, gain);
    },
    []
  );

  const getUser = useCallback((userId: string) => {
    return quickvoRef.current?.getUser(userId);
  }, []);

  const getUserStream = useCallback((userId: string, mediaType: MediaType) => {
    return quickvoRef.current?.getUserStream(userId, mediaType);
  }, []);

  const setLocalStream = useCallback(async (mediaType: MediaType, open: boolean) => {
    return await quickvoRef.current?.setLocalStream(mediaType, open);
  }, []);

  const getMediaDevicesErrInfo = useCallback(async () => {
    const { microphoneCamera_audio, microphoneCamera_video } =
      await quickvoRef.current!.getMediaDevicesErrInfo();
    return { microphoneCamera_audio, microphoneCamera_video };
  }, []);

  const getEnumerateDevices = useCallback((mediaDevice: MediaDeviceKind) => {
    return quickvoRef.current?.getEnumerateDevices(mediaDevice);
  }, []);

  const getMediaDeviceKind = useCallback((mediaDevice: MediaDeviceKind) => {
    return quickvoRef.current?.getMediaDeviceKind(mediaDevice);
  }, []);

  const setMediaDeviceKind = useCallback(
    (mediaDevice: MediaDeviceKind, deviceId: string) => {
      return quickvoRef.current?.setMediaDeviceKind(mediaDevice, deviceId);
    },
    []
  );

  const inactiveTracks = useCallback((mediaType: MediaType, enabled: boolean) => {
    return quickvoRef.current?.inactiveTracks([mediaType], enabled);
  }, []);

  const getDevicesData = useCallback(async () => {
    const [devicesVideoInput = [], devicesAudioInput = [], devicesAudioOutput = []] =
      await Promise.all([
        quickvoRef.current?.getEnumerateDevices(MediaDeviceKind.VIDEO_INPUT),
        quickvoRef.current?.getEnumerateDevices(MediaDeviceKind.AUDIO_INPUT),
        quickvoRef.current?.getEnumerateDevices(MediaDeviceKind.AUDIO_OUTPUT),
      ]);

    pushLog('quickVO:getDevicesData', {
      devicesVideoInput,
      devicesAudioInput,
      devicesAudioOutput,
    });

    setDevices({
      devicesVideoInput,
      devicesAudioInput,
      devicesAudioOutput,
    });
  }, []);

  const drawSpectrum = useCallback(
    (
      userId: string,
      mediaType: MediaType.MICROPHONECAMERA_AUDIO,
      canvas: HTMLCanvasElement | undefined,
      frameId: string
    ) => {
      if (!canvas) return;
      const userAudioContext = quickvoRef.current?.getUserAudioMediaStreamContext(
        userId,
        mediaType
      );

      const { analyserNode, analyserArrayData: array = new Uint8Array(255) } =
        userAudioContext ?? {};

      const cwidth = canvas.width;
      const cheight = canvas.height;
      const cpadding = 1;

      const meterWidth = 1.5;
      const meterGap = 1.5;
      const meterY = cheight / 2;
      const meterWidthWithGap = meterWidth + meterGap;
      const meterNum = Math.floor(
        (cwidth - cpadding * 2 + meterGap) / meterWidthWithGap
      );
      //array as Uint8Array 0-255 128
      const meterRatio = cheight / 128;

      const ctx = canvas.getContext('2d')!;

      const draw = () => {
        analyserNode?.getByteFrequencyData(array);
        const step = Math.round(array.length / meterNum);
        ctx.clearRect(0, 0, cwidth, cheight);

        for (let i = 0; i < meterNum; i++) {
          const value = array[i * step];
          const meterX = cpadding + meterGap / 2 + i * meterWidthWithGap;
          const meterHeight = value ? (value * meterRatio) / 2 : 2;

          ctx.fillStyle = '#00b600';
          ctx.beginPath();
          ctx.roundRect(meterX, meterY, meterWidth, meterHeight, [0, 0, 2, 2]);
          ctx.roundRect(meterX, meterY, meterWidth, -meterHeight, [0, 0, 2, 2]);
          ctx.closePath();
          ctx.fill();
        }
      };
      quickvoRef.current?.addRequestAnimationFrame(frameId, draw);
    },
    []
  );

  const clearRequestAnimationFrame = useCallback((frameId: string) => {
    quickvoRef.current?.clearRequestAnimationFrame([frameId]);
  }, []);

  const initNotify = useCallback(() => {
    quickvoRef.current?.addNotify({
      event: 'onRoomUsers',
      callback: async (res: SendMessageRevolve<RoomUser[]>) => {
        pushLog('quickVO:onRoomUsers', res);
        setRoomUsers(res.data);
      },
    });

    quickvoRef.current?.addNotify({
      event: 'onRoomUsersStreams',
      callback: async (res: SendMessageRevolve<RoomUsersStreams[]>) => {
        pushLog('quickVO:onRoomUsersStreams', res);
        setRoomUsersStreams(res.data);
      },
    });

    quickvoRef.current?.addNotify({
      event: 'onPublish',
      callback: async (e) => {
        quickvoRef.current?.subscribe(
          e.data.user.tracks.map((track: UserTrack) => track.trackName)
        );
      },
    });

    quickvoRef.current?.addNotify({
      event: 'onDevicechange',
      callback: async (res) => {
        pushLog('quickVO:onDevicechange', res);
        getDevicesData();
      },
    });

    quickvoRef.current?.addNotify({
      event: 'onRoomState',
      callback: async (res) => {
        const { state } = res.data;
        if (state === 'closed') {
          message.error('Network connection timeout');
          setRefresh((prev) => !prev);
        }
      },
    });
  }, [getDevicesData]);

  const updateUserStreams = useCallback(
    async ({
      user,
      mediaDoms,
    }: {
      user: Pick<RoomUser, 'id' | 'updateStreams'>;
      mediaDoms: {
        videoRef?: React.RefObject<HTMLVideoElement>;
        audioRef?: React.RefObject<HTMLAudioElement>;
        screenVideoRef?: React.RefObject<HTMLVideoElement>;
      };
    }) => {
      const { id } = user;
      const { audioRef, videoRef, screenVideoRef } = mediaDoms;

      const mediaTypeMap: Record<
        MediaType,
        HTMLVideoElement | HTMLAudioElement | undefined | null
      > = {
        [MediaType.MICROPHONECAMERA_AUDIO]: audioRef?.current,
        [MediaType.MICROPHONECAMERA_VIDEO]: videoRef?.current,
        [MediaType.SCREENSHARING_AUDIO]: audioRef?.current,
        [MediaType.SCREENSHARING_VIDEO]: screenVideoRef?.current,
      };

      const updateStream = async (mediaType: MediaType) => {
        const dom = mediaTypeMap[mediaType];
        if (!dom) return;
        try {
          const stream = await quickvoRef.current?.getUserStream(id, mediaType);
          if (stream && dom) {
            dom.srcObject = stream;
            dom.load();
            await dom.play();
          }
        } catch (error) {
          console.error(`Error ${dom} stream for type ${mediaType}:`, error);
        }
      };

      await Promise.allSettled([
        updateStream(MediaType.MICROPHONECAMERA_AUDIO),
        updateStream(MediaType.MICROPHONECAMERA_VIDEO),
        updateStream(MediaType.SCREENSHARING_AUDIO),
        updateStream(MediaType.SCREENSHARING_VIDEO),
      ]);
    },
    []
  );

  useEffect(() => {
    if (
      roomUsers.length === 1 &&
      roomUsers?.find(({ id }) => id === user?.userId) &&
      !chatMediaCall.chatMediaInfo
    ) {
      //quitRoomEx();
    }
  }, [chatMediaCall.chatMediaInfo, quitRoomEx, roomUsers, user?.userId]);

  useEffect(() => {
    quickvoRef.current = new QuickVO({
      debug: isDebug,
      appid: import.meta.env.VITE_QUICKVO_APP_ID,
      url: import.meta.env.VITE_QUICKVO_URL_WS,
    });
    if (isDebug) {
      console.log(import.meta.env.VITE_QUICKVO_APP_ID);
      window.Quickvo = quickvoRef.current;
    }
    initNotify();
    getDevicesData();
    quickvoRef.current.setOptions({ userId: user?.userId });
    quickvoRef.current.earlyConnect();
    return () => {
      pushLog('quickVO:quickvo destroy');
      quickvoRef.current?.destroy();
      quickvoRef.current = null;
      clearState();
    };
  }, [initNotify, getDevicesData, clearState, user?.userId, refresh]);

  return {
    quickvo: quickvoRef.current,
    roomUsers,
    roomUsersStreams,
    devices,
    getUser,
    getUserStream,
    joinRoom,
    quitRoom,
    quitRoomEx,
    publish,
    stopPublish,
    subscribe,
    stopSubscribe,
    getVolume,
    setVolume,
    getMediaDevicesErrInfo,
    setLocalStream,
    updateUserStreams,
    getEnumerateDevices,
    getMediaDeviceKind,
    setMediaDeviceKind,
    inactiveTracks,
    drawSpectrum,
    clearRequestAnimationFrame,
  };
}
